import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getAdminSettings, updateAdminSettings } from '../api';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const SETTING_LABELS = {
  platform_name: { label: 'Nom de la plateforme', type: 'text', placeholder: 'YetuTv' },
  tagline: { label: 'Slogan', type: 'text', placeholder: 'Ya Biso' },
  points_per_referral: { label: 'Points par parrainage', type: 'number', placeholder: '50' },
  min_payout_amount: { label: 'Paiement minimum ($)', type: 'number', placeholder: '10' },
  auto_approve_videos: { label: 'Auto-approuver les vidéos', type: 'boolean' },
  maintenance_mode: { label: 'Mode maintenance', type: 'boolean' },
  max_upload_size_mb: { label: 'Taille max upload (Mo)', type: 'number', placeholder: '500' },
  free_trial_days: { label: 'Jours essai gratuit', type: 'number', placeholder: '7' },
  contact_email: { label: 'Email de contact', type: 'email', placeholder: 'contact@yetutv.cd' },
  support_whatsapp: { label: 'WhatsApp support', type: 'text', placeholder: '+243...' },
};

export const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getAdminSettings();
        setSettings(res.data.settings || {});
      } catch {
        toast.error('Erreur chargement paramètres');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAdminSettings(settings);
      toast.success('Paramètres enregistrés');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <AdminLayout><div className="py-20 text-center text-muted-foreground">Chargement...</div></AdminLayout>;
  }

  const knownKeys = Object.keys(SETTING_LABELS);
  const extraKeys = Object.keys(settings).filter(k => !knownKeys.includes(k));

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Paramètres de la plateforme</h1>
          <p className="text-muted-foreground text-sm">Configuration générale de YetuTv</p>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            {knownKeys.map(key => {
              const meta = SETTING_LABELS[key];
              const val = settings[key] ?? '';
              return (
                <div key={key}>
                  <label className="text-sm font-medium text-foreground block mb-1">{meta.label}</label>
                  {meta.type === 'boolean' ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleChange(key, !val)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${val ? 'bg-primary' : 'bg-muted'}`}
                      >
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${val ? 'translate-x-6' : ''}`} />
                      </button>
                      <span className="text-sm text-muted-foreground">{val ? 'Activé' : 'Désactivé'}</span>
                    </div>
                  ) : (
                    <input
                      type={meta.type}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={meta.placeholder || ''}
                      value={val}
                      onChange={e => handleChange(key, meta.type === 'number' ? Number(e.target.value) : e.target.value)}
                    />
                  )}
                </div>
              );
            })}

            {/* Paramètres inconnus (créés dynamiquement) */}
            {extraKeys.map(key => (
              <div key={key}>
                <label className="text-sm font-medium text-foreground block mb-1">{key}</label>
                <input
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  value={settings[key] ?? ''}
                  onChange={e => handleChange(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </form>
      </div>
    </AdminLayout>
  );
};
